import React, { useEffect, useState } from 'react';

function computeBabyAgeGroup(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  const monthDiff = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (monthDiff < 6) return '0-6_ay';
  if (monthDiff < 12) return '6-12_ay';
  if (monthDiff < 24) return '12-24_ay';
  return '24_ay_plus';
}

const BabyModal = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = useState('choice');
  const [babyName, setBabyName] = useState('');
  const [babyBirthDate, setBabyBirthDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep('choice');
      setBabyName('');
      setBabyBirthDate('');
      setSaving(false);
    }
  }, [isOpen]);

  const submitAndClose = async (payload) => {
    try {
      setSaving(true);
      await onSubmit(payload);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  const handleNoClick = async () => {
    await submitAndClose({
      hasBaby: false,
      babyBirthDate: null,
      babyName: null,
      babyAgeGroup: null,
      babyModalCompleted: true
    });
  };

  const handleSaveDetails = async () => {
    const trimmedName = String(babyName || '').trim();
    const birthDate = String(babyBirthDate || '').trim();
    if (!trimmedName || !birthDate) return;

    await submitAndClose({
      hasBaby: true,
      babyName: trimmedName,
      babyBirthDate: birthDate,
      babyAgeGroup: computeBabyAgeGroup(birthDate),
      babyModalCompleted: true
    });
  };

  const handleLater = async (reason) => {
    await submitAndClose({
      hasBaby: true,
      babyName: null,
      babyBirthDate: null,
      babyAgeGroup: null,
      babyModalCompleted: false,
      babyDetailDeferredReason: reason
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm transition-all animate-in fade-in">
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-white p-6 shadow-2xl dark:border-white/5 dark:bg-slate-900">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
        >
          <span className="material-symbols-outlined text-[22px]">close</span>
        </button>

        <div className="mb-6 text-center">
          <div className="mb-3 text-4xl">👶</div>
          {step === 'choice' ? (
            <>
              <h2 className="mb-2 text-xl font-extrabold tracking-tight text-gray-800 dark:text-white">
                Küçük bir gurme için de pişirelim mi?
              </h2>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Eğer bu yemeği bir bebekle paylaşacaksan, bunu işaretleyelim.
              </p>
            </>
          ) : (
            <>
              <h2 className="mb-2 text-xl font-extrabold tracking-tight text-gray-800 dark:text-white">
                Bebeğini daha iyi tanıyalım
              </h2>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Tarifleri daha doğru kişiselleştirmek için isim ve doğum tarihini paylaşabilirsin.
              </p>
            </>
          )}
        </div>

        {step === 'choice' ? (
          <div className="space-y-3">
            <button
              onClick={handleNoClick}
              disabled={saving}
              className="w-full rounded-lg bg-gray-100 p-4 font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-60"
            >
              Hayır, sadece yetişkinler için
            </button>

            <button
              onClick={() => setStep('details')}
              disabled={saving}
              className="w-full rounded-lg bg-pink-100 p-4 font-medium text-pink-800 transition-colors hover:bg-pink-200 disabled:opacity-60"
            >
              Evet, paylaşacağız
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-gray-700 dark:text-gray-300">Bebek adı</label>
              <input
                type="text"
                value={babyName}
                onChange={(e) => setBabyName(e.target.value)}
                maxLength={40}
                placeholder="Örn: Defne"
                className="w-full rounded-xl border border-[#e6dbdb] bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-gray-700 dark:text-gray-300">Doğum tarihi</label>
              <input
                type="date"
                max={new Date().toISOString().split('T')[0]}
                value={babyBirthDate}
                onChange={(e) => setBabyBirthDate(e.target.value)}
                className="w-full rounded-xl border border-[#e6dbdb] bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-white"
              />
            </div>

            <button
              type="button"
              onClick={handleSaveDetails}
              disabled={saving || !String(babyName || '').trim() || !String(babyBirthDate || '').trim()}
              className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>

            <button
              type="button"
              onClick={() => handleLater('later')}
              disabled={saving}
              className="w-full rounded-xl border border-[#e6dbdb] bg-white px-4 py-3 text-sm font-bold text-slate-700 dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-slate-200 disabled:opacity-60"
            >
              Daha Sonra
            </button>

            <button
              type="button"
              onClick={() => handleLater('decline')}
              disabled={saving}
              className="w-full rounded-xl border border-[#e6dbdb] bg-white px-4 py-3 text-sm font-bold text-slate-700 dark:border-gray-700 dark:bg-[#0b0b0b] dark:text-slate-200 disabled:opacity-60"
            >
              Bu Bilgiyi Girmek İstemiyorum
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BabyModal;
